import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { Button } from '@components/button';
import { Checkbox } from '@components/checkbox';
import { PasswordField } from '@components/password-field';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';

import { api } from 'api';

import { Box } from 'components/Box';
import { CountryCode } from 'components/CountryCode';
import { UnauthedPage } from 'components/unauthed-page';
import { useStore } from 'models/Provider';

export const SetPasswordTwoFactor = observer((props) => {
  const { t } = useStore();
  const { qrCodeUrl, uuid, passwordUuid } = props;
  const client = useQueryClient();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [hasAuthenticatorApp, setHasAuthenticatorApp] = useState(false);
  const [countryCode, setCountryCode] = useState('IS +354');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [state, setState] = useState({
    password1: '',
    password2: '',
    password1Error: '',
    password2Error: '',
    code: null,
    codeError: '',
    qrCodeUrl: '',
    phone: '',
    disable: false,
  });

  function onChange(name, value) {
    setState((old) => ({ ...old, [name]: value }));
  }

  function updatePhone(event) {
    onChange('phone', countryCode.substr(3) + phoneNumber);
  }

  function validatePassword() {
    if (state.password1 === '' || state.password2 === '') {
      if (state.password1 === '') {
        setState((old) => ({
          ...old,
          password1Error: t('setpw.twofactor.step2.password.required'),
        }));
      } else {
        setState((old) => ({
          ...old,
          password1Error: '',
        }));
      }
      if (state.password2 === '') {
        setState((old) => ({
          ...old,
          password2Error: t('setpw.twofactor.step2.password.required'),
        }));
      } else {
        setState((old) => ({
          ...old,
          password2Error: '',
        }));
      }
    } else if (state.password1 !== state.password2) {
      setState((old) => ({
        ...old,
        password1Error: ' ',
        password2Error: t('setpw.twofactor.step2.password.nomatch'),
      }));
    } else {
      setStep(3);
    }
  }

  async function setPassword() {
    setState((old) => ({ ...old, error: '', disable: true }));

    const res = await api.users.setPassword({
      password1: state.password1,
      password2: state.password2,
      uuid,
      passwordUuid,
      code: state.code,
      phone: state.phone,
    });
    if (res.error) {
      setState((old) => ({
        ...old,
        password1Error: res.data.password1 ? res.data.password1.join('\n') : '',
        password2Error: res.data.password2 ? res.data.password2.join('\n') : '',
        codeError: res.data.code ? res.data.code.join('\n') : '',
        disable: false,
      }));
      if (res.data.password1 || res.data.password2) {
        setStep(2);
      }
    } else {
      setStep(4);
    }
  }

  return (
    <UnauthedPage>
      {step === 1 && (
        <Stack space={6}>
          <div>
            <Text font="heading1" text={t('setpw.twofactor.step1.heading')} />
            <Text text={t('setpw.twofactor.step1.instruction')} />
            <Checkbox
              value={hasAuthenticatorApp}
              onChange={() => setHasAuthenticatorApp(!hasAuthenticatorApp)}
              label={t('setpw.twofactor.step1.hasauthenticatorapp')}
            />
          </div>
          <Button
            isDisabled={!hasAuthenticatorApp}
            onClick={() => setStep(2)}
            text={t('setpw.twofactor.step1.submit')}
          />
        </Stack>
      )}
      {step === 2 && (
        <>
          <StepHeader activeStep={1} />
          <Stack space={6}>
            <Stack space={4}>
              <Text font="heading1" text={t('setpw.twofactor.step2.heading')} />
              <PasswordField
                label={t('setpw.label.password')}
                value={state.password1}
                onChange={(val) => onChange('password1', val)}
                error={
                  state.password1Error &&
                  state.password1Error.split('\n').map((item, i) => <div key={i}>{item}</div>)
                }
                isDisabled={state.disable}
              />
              <PasswordField
                label={t('setpw.label.confirm')}
                value={state.password2}
                onChange={(val) => onChange('password2', val)}
                error={
                  state.password2Error &&
                  state.password2Error.split('\n').map((item, i) => <div key={i}>{item}</div>)
                }
                isDisabled={state.disable}
              />
            </Stack>
            <Button
              isDisabled={!hasAuthenticatorApp}
              onClick={validatePassword}
              text={t('setpw.twofactor.step2.submit')}
            />
          </Stack>
        </>
      )}
      {step === 3 && (
        <>
          <StepHeader activeStep={2} />
          <Stack space={6}>
            <Stack space={4}>
              <Text font="heading1" text={t('setpw.twofactor.step3.heading')} />
              <Text text={t('setpw.twofactor.step3.qrcode.helptext')} />
              <QRCodeContainer>
                <img alt={'qrcode'} style={{ width: '50%' }} src={qrCodeUrl} />
              </QRCodeContainer>
              <Text text={t('setpw.twofactor.step3.code.helptext')} />
              <TextField
                label={t('setpw.twofactor.step3.code.label')}
                value={state.code}
                onChange={(val) => onChange('code', val)}
                error={
                  state.codeError &&
                  state.codeError.split('\n').map((item, i) => <div key={i}>{item}</div>)
                }
                isDisabled={state.disable}
              />
            </Stack>
            <Stack space={3}>
              <Text text={t('setpw.twofactor.step3.phone.helptext')} />
              <Box w={160}>
                <CountryCode
                  label={t('setpw.twofactor.step3.countrycode.label')}
                  onChange={(e) => {
                    setCountryCode(e);
                    updatePhone(e);
                  }}
                  defaultValue={countryCode}
                />
              </Box>
              <Box ml={20}>
                <TextField
                  label={t('setpw.twofactor.step3.phone.label')}
                  value={phoneNumber}
                  onChange={(e) => {
                    setPhoneNumber(e);
                    updatePhone(e);
                  }}
                />
              </Box>
            </Stack>
            <Button
              isDisabled={!hasAuthenticatorApp}
              onClick={setPassword}
              text={t('setpw.twofactor.step3.submit')}
            />
          </Stack>
        </>
      )}
      {step === 4 && (
        <>
          <Stack space={6}>
            <Stack space={2}>
              <Text font="heading1" text={t('setpw.twofactor.step4.heading')} />
              <Text color="purple60" text={t('setpw.twofactor.step4.subheading')} />
            </Stack>
            <Button
              onClick={() => {
                client.resetQueries();
                navigate('/');
              }}
              text={t('setpw.twofactor.step4.submit')}
            />
          </Stack>
        </>
      )}
    </UnauthedPage>
  );
});

const QRCodeContainer = styled.div((p) => ({
  display: 'flex',
  backgroundColor: p.theme.colors.purpleTrans5,
  justifyContent: 'center',
  width: '100%',
}));

const StepHeader = ({ job, activeStep }) => {
  const { t } = useStore();
  return (
    <Container>
      <Steps>
        <Step step={1} label={t('setpw.twofactor.step1.label')} active={activeStep === 1} />
        <StepLine />
        <Step step={2} label={t('setpw.twofactor.step2.label')} active={activeStep === 2} />
      </Steps>
    </Container>
  );
};

const Step = ({ step, label, active, link = undefined }) => {
  const el = (
    <Box flex alignItems="center">
      <StepBubble active={active}>{step}</StepBubble>
      <StepLabel active={active}>{label}</StepLabel>
    </Box>
  );
  if (link === undefined) return el;
};

const StepLine = styled.div((p) => ({
  height: '0.0625rem',
  margin: '0 1.5rem',
  width: '2rem',
  backgroundColor: p.theme.colors.purpleTrans20,
}));

const Steps = styled.div((p) => ({
  maxWidth: '62rem',
  height: '100%',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
}));

const Container = styled.div((p) => ({
  width: '100%',
  height: '4.5rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StepLabel = styled.div((p) => ({
  flexShrink: 0,
  marginLeft: p.theme.spacing.m8,
  color: p.active ? p.theme.colors.green100 : p.theme.colors.purple100,
  ...p.theme.text.bold14,
  opacity: p.active ? 1 : 0.5,
}));

const StepBubble = styled.div((p) => ({
  flexShrink: 0,
  height: '1.5rem',
  width: '1.5rem',
  borderRadius: '50%',
  backgroundColor: p.active ? p.theme.colors.green100 : p.theme.colors.purple80,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: p.theme.colors.white100,
  ...p.theme.text.bold12,
}));
